import React from 'react'
import { Box, Container, Grid, Typography, Button, Paper } from '@mui/material'
import { styled, createTheme, ThemeProvider, alpha } from '@mui/material/styles'
import {
  Balance,
  Description,
  Security,
  People,
  Warning,
  Email,
  Launch,
  Visibility,
  VerifiedUser,
  Settings,
  Message,
  FlashOn,
  Notifications,
  CreditCard,
  Block,
  Article,
  Shield,
  Campaign,
  PowerOff,
  Gavel,
  Assignment,
  CheckCircle,
  Public,
} from '@mui/icons-material'

const themeColors = {
  primary: '#232536',
  secondary: '#ffcf51',
  hover: '#3a3e5a',
}

const theme = createTheme({
  palette: {
    primary: {
      main: '#ffda1b',
      contrastText: '#23252a',
    },
    secondary: {
      main: '#23252a',
      contrastText: '#ffffff',
    },
    background: {
      default: '#f3f4f6',
      paper: '#ffffff',
    },
    text: {
      primary: '#23252a',
      secondary: '#64748b',
    },
  },
  typography: {
    fontFamily: 'Inter, sans-serif',
    h1: {
      fontSize: '3rem',
      fontWeight: 'bold',
      '@media (max-width:768px)': {
        fontSize: '2rem',
      },
      '@media (max-width:600px)': {
        fontSize: '1.875rem',
      },
    },
    h2: {
      fontSize: '1.5rem',
      fontWeight: 'bold',
      '@media (max-width:600px)': {
        fontSize: '1.25rem',
      },
    },
  },
  shape: {
    borderRadius: 12,
  },
})

const HeroSection = styled(Box)(({ theme }) => ({
  background: `linear-gradient(135deg, #23252a 0%, #374151 100%)`,
  color: 'white',
  padding: theme.spacing(6, 0),
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(8, 0),
  },
  [theme.breakpoints.up('lg')]: {
    padding: theme.spacing(10, 0),
  },
}))

const ContactSection = styled(Box)(({ theme }) => ({
  background: `linear-gradient(90deg, #23252a 0%, #374151 100%)`,
  borderRadius: theme.spacing(1.5),
  padding: theme.spacing(3),
  color: 'white',
  marginTop: theme.spacing(6),
  [theme.breakpoints.up('sm')]: {
    borderRadius: theme.spacing(2),
    padding: theme.spacing(4),
    marginTop: theme.spacing(8),
  },
}))

const GoldDivider = styled(Box)(({ theme }) => ({
  width: '64px',
  height: '4px',
  backgroundColor: '#ffda1b',
  margin: '0 auto',
  marginBottom: theme.spacing(3),
  [theme.breakpoints.up('sm')]: {
    width: '96px',
    marginBottom: theme.spacing(4),
  },
}))

const NavigationButton = styled(Button)(({ theme }) => ({
  padding: theme.spacing(1.5),
  borderRadius: theme.spacing(1.5),
  textTransform: 'none',
  justifyContent: 'flex-start',
  backgroundColor: 'white',
  color: '#23252a',
  border: '1px solid #e5e7eb',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    backgroundColor: 'white',
    borderColor: alpha(themeColors.secondary, 0.45),
    '& .nav-icon-container': {
      backgroundColor: themeColors.hover,
      color: themeColors.secondary,
      '& .MuiSvgIcon-root': {
        color: `${themeColors.secondary} !important`,
      },
    },
    '& .nav-text': {
      color: themeColors.primary,
    },
  },
}))

const IconContainer = styled(Box)(({ theme }) => ({
  backgroundColor: themeColors.hover,
  color: themeColors.secondary,
  padding: theme.spacing(1),
  borderRadius: theme.spacing(1),
  marginRight: theme.spacing(1.5),
  display: 'flex',
  alignItems: 'center',
  width: 40,
  height: 40,
  justifyContent: 'center',
  transition: 'background-color 0.3s ease, color 0.3s ease',
  '& .MuiSvgIcon-root': {
    color: `${themeColors.secondary} !important`,
  },
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(1.5),
    marginRight: theme.spacing(2),
    width: 40,
    height: 40,
  },
}))

const SectionContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
  [theme.breakpoints.up('sm')]: {
    marginBottom: theme.spacing(6),
  },
}))

const SectionHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: theme.spacing(2),
  [theme.breakpoints.up('sm')]: {
    marginBottom: theme.spacing(3),
  },
}))

const ContentBox = styled(Paper)(({ theme }) => ({
  backgroundColor: '#f3f4f6',
  boxShadow: 'none',
  borderRadius: theme.spacing(1.5),
  padding: theme.spacing(2),
  borderLeft: '4px solid #ffda1b',
  marginBottom: theme.spacing(1.5),
  '& ol, & ul': {
    marginTop: 0,
    marginBottom: 0,
    paddingLeft: theme.spacing(5.5),
    listStyleType: 'lower-alpha',
  },
  '& ol ol, & ol ul, & ul ol, & ul ul': {
    paddingLeft: theme.spacing(7),
    listStyleType: 'lower-roman',
  },
}))

const linkStyle = {
  color: '#ffda1b',
  fontWeight: 'bold',
  cursor: 'pointer',
  textDecoration: 'none',
  '&:hover': { textDecoration: 'underline' },
}

const highlightedLinkStyle = {
  display: 'inline-block',
  px: 1,
  py: 0.2,
  borderRadius: 1,
  backgroundColor: alpha(themeColors.secondary, 0.25),
  color: themeColors.primary,
  fontWeight: 'bold',
  cursor: 'pointer',
  textDecoration: 'none',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: themeColors.hover,
    color: themeColors.secondary,
    textDecoration: 'none',
  },
}

const TermsAndConditions = () => {
  const sections = [
    {
      id: 'definitions',
      title: 'Definitions',
      icon: <Description sx={{ fontSize: 16 }} />,
    },
    {
      id: 'nature-content',
      title: 'Nature of content and authoritative sources',
      icon: <Article sx={{ fontSize: 16 }} />,
    },
    {
      id: 'no-legal-advice',
      title: 'No legal, tax, or professional advice',
      icon: <Balance sx={{ fontSize: 16 }} />,
    },
    {
      id: 'eligibility',
      title: 'Eligibility and account registration',
      icon: <People sx={{ fontSize: 16 }} />,
    },
    {
      id: 'subscription',
      title: 'Subscription plans, billing, and payments (Stripe)',
      icon: <CreditCard sx={{ fontSize: 16 }} />,
    },
    {
      id: 'permitted-use',
      title: 'Permitted use and licence',
      icon: <CheckCircle sx={{ fontSize: 16 }} />,
    },
    {
      id: 'prohibited-activities',
      title: 'Prohibited activities and acceptable use',
      icon: <Block sx={{ fontSize: 16 }} />,
    },
    {
      id: 'intellectual-property',
      title: 'Intellectual property',
      icon: <Security sx={{ fontSize: 16 }} />,
    },
    {
      id: 'user-content',
      title: 'User-generated content and feedback',
      icon: <Message sx={{ fontSize: 16 }} />,
    },
    {
      id: 'third-party',
      title: 'Third-party links, authentication, and services',
      icon: <Launch sx={{ fontSize: 16 }} />,
    },
    {
      id: 'advertising',
      title: 'Advertising and promotions',
      icon: <Campaign sx={{ fontSize: 16 }} />,
    },
    {
      id: 'disclaimer',
      title: 'Disclaimer of warranties',
      icon: <Warning sx={{ fontSize: 16 }} />,
    },
    {
      id: 'limitation-liability',
      title: 'Limitation of liability',
      icon: <Shield sx={{ fontSize: 16 }} />,
    },
    {
      id: 'indemnification',
      title: 'Indemnification',
      icon: <VerifiedUser sx={{ fontSize: 16 }} />,
    },
    {
      id: 'suspension',
      title: 'Suspension and termination',
      icon: <PowerOff sx={{ fontSize: 16 }} />,
    },
    {
      id: 'changes-services',
      title: 'Changes to the Services and Content',
      icon: <Settings sx={{ fontSize: 16 }} />,
    },
    {
      id: 'beta-services',
      title: 'Beta services',
      icon: <FlashOn sx={{ fontSize: 16 }} />,
    },
    {
      id: 'privacy-policy',
      title: 'Privacy policy and cookie policy',
      icon: <Visibility sx={{ fontSize: 16 }} />,
    },
    {
      id: 'notices',
      title: 'Notices and communications',
      icon: <Notifications sx={{ fontSize: 16 }} />,
    },
    {
      id: 'governing-law',
      title: 'Governing law and jurisdiction',
      icon: <Gavel sx={{ fontSize: 16 }} />,
    },
    {
      id: 'severability',
      title: 'Severability',
      icon: <Assignment sx={{ fontSize: 16 }} />,
    },
    {
      id: 'entire-agreement',
      title: 'Entire agreement',
      icon: <Public sx={{ fontSize: 16 }} />,
    },
    { id: 'contact', title: 'Contact', icon: <Email sx={{ fontSize: 16 }} /> },
  ]

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' })
  }

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ minHeight: '100vh', backgroundColor: 'background.default' }}>
        <HeroSection>
          <Container maxWidth='lg'>
            {/* --- Start Date Section --- */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end', // Pushes content to the right
                mb: { xs: 2, sm: 4 }, // Margin bottom to separate from the icon
                width: '100%',
              }}
            >
              <Box sx={{ textAlign: 'left' }}>
                {[
                  { label: 'Effective Date:', date: '14 February 2026' },
                  { label: 'Last Updated:', date: '14 February 2026' },
                ].map((item, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      gap: 1, // Space between label and date
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 'bold',
                        color: '#e2e8f0',
                        fontSize: { xs: '0.85rem', sm: '1rem' },
                        width: { xs: '110px', sm: '130px' }, // Fixed width for vertical alignment
                      }}
                    >
                      {item.label}
                    </Typography>
                    <Typography
                      sx={{
                        color: '#e2e8f0',
                        fontSize: { xs: '0.85rem', sm: '1rem' },
                      }}
                    >
                      {item.date}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
            {/* --- End Date Section --- */}
            <Box sx={{ textAlign: 'center' }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  mb: 3,
                }}
              >
                <Box
                  sx={{
                    backgroundColor: themeColors.hover,
                    p: { xs: 1.5, sm: 2 },
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Balance
                    sx={{
                      fontSize: { xs: 32, sm: 48 },
                      color: themeColors.secondary,
                    }}
                  />
                </Box>
              </Box>
              <Typography
                variant='h1'
                sx={{
                  mb: 3,
                  fontSize: {
                    xs: '1.875rem',
                    sm: '2.5rem',
                    md: '3rem',
                    lg: '3.75rem',
                  },
                }}
              >
                Terms and Conditions
              </Typography>
              <GoldDivider />
              <Typography
                variant='h6'
                sx={{
                  mb: 1,
                  color: '#e2e8f0',
                  maxWidth: '900px',
                  mx: 'auto',
                  lineHeight: 1.6,
                  px: 2,
                  fontSize: { xs: '1.125rem', sm: '1.25rem' },
                }}
              >
                Welcome to {/* Yellow + Bold + Link Style */}
                <Box
                  component='span'
                  onClick={() =>
                    window.open('https://gcctaxlaws.com', '_blank')
                  }
                  sx={{
                    color: '#ffda1b',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  GCCTaxLaws.com
                </Box>
                . These Terms and Conditions (the “Terms”) govern your access to
                and use of{' '}
                <Box component='span' sx={{ fontWeight: 'bold' }}>
                  {/* Just Bold */}
                  gcctaxlaws.com
                </Box>{' '}
                and any related pages, applications, content, and services
                (collectively, the “Website” or “Services”).
              </Typography>
              <Typography
                variant='h6'
                sx={{
                  mb: 1,
                  color: '#e2e8f0',
                  maxWidth: '900px',
                  mx: 'auto',
                  lineHeight: 1.6,
                  px: 2,
                  fontSize: { xs: '1.125rem', sm: '1.25rem' },
                }}
              >
                The Website is owned and operated by{' '}
                <strong>Claritax Technology Solutions LLC FZ</strong> (referred
                to as “GCCTaxLaws.com”, “we”, “our”, “us”), with registered
                address at Meydan Free Zone,{' '}
                <strong>
                  Meydan Grandstand, 6th Floor, Al Meydan Rd, Nad Al Sheba,
                  Dubai, United Arab Emirates.
                </strong>
              </Typography>
              <Typography
                variant='h6'
                sx={{
                  mb: 1,
                  color: '#e2e8f0',
                  maxWidth: '900px',
                  mx: 'auto',
                  lineHeight: 1.6,
                  px: 2,
                  fontSize: { xs: '1.125rem', sm: '1.25rem' },
                }}
              >
                <strong>Contact</strong>:{' '}
                <Box
                  component='span'
                  // Using location.href prevents an unnecessary blank tab from opening
                  onClick={() =>
                    (window.location.href = 'mailto:contact@gcctaxlaws.com')
                  }
                  sx={{
                    color: '#ffda1b',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  contact@gcctaxlaws.com
                </Box>
              </Typography>
              <Typography
                variant='h6'
                sx={{
                  // mb: 1,
                  color: '#e2e8f0',
                  maxWidth: '900px',
                  mx: 'auto',
                  lineHeight: 1.6,
                  px: 2,
                  fontSize: { xs: '1.125rem', sm: '1.25rem' },
                }}
              >
                By accessing or using the Services, you agree to be bound by
                these Terms. If you do not agree, you must stop using the
                Services immediately.
              </Typography>
            </Box>
          </Container>
        </HeroSection>

        <Box
          sx={{
            backgroundColor: '#fef2f2',
            py: { xs: 3, sm: 4 },
            borderLeft: '4px solid #ef4444',
          }}
        >
          <Container maxWidth='lg'>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: { xs: 1.5, sm: 2 },
              }}
            >
              <Box
                sx={{
                  backgroundColor: '#fecaca',
                  p: { xs: 1, sm: 1.5 },
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Warning
                  sx={{ fontSize: { xs: 20, sm: 24 }, color: '#dc2626' }}
                />
              </Box>
              <Box>
                <Typography
                  variant='h6'
                  sx={{
                    color: '#991b1b',
                    fontWeight: 'bold',
                    mb: 1,
                    fontSize: { xs: '1.125rem', sm: '1.25rem' },
                  }}
                >
                  Binding Agreement
                </Typography>
                <Typography
                  variant='body2'
                  sx={{
                    color: '#b91c1c',
                    lineHeight: 1.6,
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                  }}
                >
                  If you do not agree to these Terms, you must discontinue using
                  the Website immediately.
                </Typography>
              </Box>
            </Box>
          </Container>
        </Box>

        <Box sx={{ py: { xs: 4, sm: 6 }, backgroundColor: 'white' }}>
          <Container maxWidth='lg'>
            <Typography
              variant='h4'
              sx={{
                fontWeight: 'bold',
                color: '#23252a',
                mb: { xs: 3, sm: 4 },
                textAlign: 'center',
                fontSize: { xs: '1.25rem', sm: '1.5rem' },
              }}
            >
              Quick Navigation
            </Typography>
            <Grid
              container
              spacing={1.5}
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, 1fr)',
                  lg: 'repeat(3, 1fr)',
                  xl: 'repeat(4, 1fr)',
                },
              }}
            >
              {sections.map((section, index) => (
                <Grid item key={index}>
                  <NavigationButton
                    fullWidth
                    onClick={() => scrollToSection(section.id)}
                  >
                    <IconContainer className='nav-icon-container'>
                      <Box sx={{ fontSize: 24, color: 'inherit' }}>
                        {section.icon}
                      </Box>
                    </IconContainer>
                    <Typography
                      className='nav-text'
                      sx={{
                        fontWeight: 'bold',
                        fontSize: '0.875rem',
                        lineHeight: 1.3,
                        textAlign: 'left',
                      }}
                    >
                      {section.title}
                    </Typography>
                  </NavigationButton>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        <Box sx={{ backgroundColor: 'white', py: { xs: 6, sm: 8 } }}>
          <Container maxWidth='lg' sx={{ maxWidth: '1000px' }}>
            <SectionContainer id='definitions'>
              <SectionHeader>
                <IconContainer>
                  <Description
                    sx={{ fontSize: { xs: 20, sm: 24 }, color: '#ffda1b' }}
                  />
                </IconContainer>
                <Typography
                  variant='h2'
                  sx={{ fontSize: { xs: '1.5rem', sm: '1.875rem' } }}
                >
                  1. Definitions
                </Typography>
              </SectionHeader>

              <ContentBox>
                <Box
                  component='ul'
                  sx={{ mb: 0, pl: 3, display: 'grid', gap: 1.25 }}
                >
                  <li>
                    <Typography
                      variant='body2'
                      sx={{ color: 'text.secondary', lineHeight: 1.7 }}
                    >
                      <strong>“Content”</strong> means all materials made
                      available through the Services, including legal texts,
                      English translations, summaries, compilations, databases,
                      annotations (if any), commentary (if any), tags, metadata,
                      layouts, graphics, and other materials.
                    </Typography>
                  </li>
                  <li>
                    <Typography
                      variant='body2'
                      sx={{ color: 'text.secondary', lineHeight: 1.7 }}
                    >
                      <strong>“Subscription”</strong> means a paid plan (daily,
                      monthly, annual) that provides access to paid features
                      and/or paid Content.
                    </Typography>
                  </li>
                  <li>
                    <Typography
                      variant='body2'
                      sx={{ color: 'text.secondary', lineHeight: 1.7 }}
                    >
                      <strong>“User”</strong> or <strong>“you”</strong> means
                      any person or entity accessing or using the Services.
                    </Typography>
                  </li>
                  <li>
                    <Typography
                      variant='body2'
                      sx={{ color: 'text.secondary', lineHeight: 1.7 }}
                    >
                      <strong>“Third-Party Services”</strong> means services not
                      controlled by us, including Google, LinkedIn, and Stripe.
                    </Typography>
                  </li>
                </Box>
              </ContentBox>
            </SectionContainer>

            <SectionContainer id='nature-content'>
              <SectionHeader>
                <IconContainer>
                  <Article
                    sx={{ fontSize: { xs: 20, sm: 24 }, color: '#ffda1b' }}
                  />
                </IconContainer>
                <Typography
                  variant='h2'
                  sx={{ fontSize: { xs: '1.5rem', sm: '1.875rem' } }}
                >
                  2. Nature of content and authoritative sources
                </Typography>
              </SectionHeader>

              <ContentBox>
                <Box
                  component='ol'
                  sx={{ mb: 0, pl: 3, display: 'grid', gap: 1.25 }}
                >
                  <li>
                    <Typography
                      variant='body2'
                      sx={{ color: 'text.secondary', lineHeight: 1.7 }}
                    >
                      The Website offers access to{' '}
                      <strong>
                        English translations of GCC tax laws originally enacted
                        in Arabic
                      </strong>
                      . These translations are provided for{' '}
                      <strong>information and convenience only</strong> and do
                      not constitute official or legally binding versions of the
                      laws.
                    </Typography>
                  </li>
                  <li>
                    <Typography
                      variant='body2'
                      sx={{ color: 'text.secondary', lineHeight: 1.7 }}
                    >
                      Some translations may be those released by governments (to
                      the extent available in the public domain), while others
                      may be privately sourced or prepared.
                    </Typography>
                  </li>
                  <li>
                    <Typography
                      variant='body2'
                      sx={{ color: 'text.secondary', lineHeight: 1.7 }}
                    >
                      The <strong>original Arabic texts</strong> of laws and
                      official publications <strong>always prevail</strong> in
                      the event of any discrepancy, inconsistency, or conflict
                      between the Arabic original and an English translation or
                      any other Content on the Website.
                    </Typography>
                  </li>
                  <li>
                    <Typography
                      variant='body2'
                      sx={{ color: 'text.secondary', lineHeight: 1.7 }}
                    >
                      You are responsible for verifying the Content against
                      official sources (including original legal texts and
                      government publications) and for obtaining professional
                      advice appropriate to your facts and jurisdiction.
                    </Typography>
                  </li>
                </Box>
              </ContentBox>
            </SectionContainer>

            <SectionContainer id='no-legal-advice'>
              <SectionHeader>
                <IconContainer>
                  <Balance
                    sx={{ fontSize: { xs: 20, sm: 24 }, color: '#ffda1b' }}
                  />
                </IconContainer>
                <Typography
                  variant='h2'
                  sx={{ fontSize: { xs: '1.5rem', sm: '1.875rem' } }}
                >
                  3. No legal, tax, or professional advice
                </Typography>
              </SectionHeader>

              <ContentBox>
                <Box
                  component='ol'
                  sx={{ mb: 0, pl: 3, display: 'grid', gap: 1.25 }}
                >
                  <li>
                    <Typography
                      variant='body2'
                      sx={{ color: 'text.secondary', lineHeight: 1.7 }}
                    >
                      The Services and Content are provided for{' '}
                      <strong>general information</strong> and do{' '}
                      <strong>not </strong>
                      constitute legal advice, tax advice, accounting advice, or
                      any other professional advice.
                    </Typography>
                  </li>
                  <li>
                    <Typography
                      variant='body2'
                      sx={{ color: 'text.secondary', lineHeight: 1.7 }}
                    >
                      You should obtain appropriate professional advice before
                      taking any action or making any decision based on the
                      Content.
                    </Typography>
                  </li>
                  <li>
                    <Typography
                      variant='body2'
                      sx={{ color: 'text.secondary', lineHeight: 1.7 }}
                    >
                      Your use of the Services does not create any
                      lawyer-client, consultant-client, fiduciary, or other
                      advisory relationship with us.
                    </Typography>
                  </li>
                </Box>
              </ContentBox>
            </SectionContainer>

            <SectionContainer id='eligibility'>
              <SectionHeader>
                <IconContainer>
                  <People
                    sx={{ fontSize: { xs: 20, sm: 24 }, color: '#ffda1b' }}
                  />
                </IconContainer>
                <Typography
                  variant='h2'
                  sx={{ fontSize: { xs: '1.5rem', sm: '1.875rem' } }}
                >
                  4. Eligibility and account registration
                </Typography>
              </SectionHeader>

              <ContentBox>
                <Box
                  component='ol'
                  sx={{ mb: 0, pl: 3, display: 'grid', gap: 1.25 }}
                >
                  <li>
                    <Typography
                      variant='body2'
                      sx={{ color: 'text.secondary', lineHeight: 1.7 }}
                    >
                      The Services are intended for users aged{' '}
                      <strong>18 and above</strong>. By using the Services, you
                      confirm that you meet this requirement.
                    </Typography>
                  </li>
                  <li>
                    <Typography
                      variant='body2'
                      sx={{ color: 'text.secondary', lineHeight: 1.7 }}
                    >
                      Certain features may require an account. We currently
                      support authentication via <strong>Google</strong> or
                      <strong> LinkedIn</strong>.
                    </Typography>
                  </li>
                  <li>
                    <Typography
                      variant='body2'
                      sx={{ color: 'text.secondary', lineHeight: 1.7 }}
                    >
                      You are responsible for maintaining the confidentiality of
                      your account access and for all activity occurring through
                      your account. You must notify us promptly if you suspect
                      unauthorized access.
                    </Typography>
                  </li>
                </Box>
              </ContentBox>
            </SectionContainer>

            <SectionContainer id='subscription'>
              <SectionHeader>
                <IconContainer>
                  <CreditCard
                    sx={{ fontSize: { xs: 20, sm: 24 }, color: '#ffda1b' }}
                  />
                </IconContainer>
                <Typography
                  variant='h2'
                  sx={{ fontSize: { xs: '1.5rem', sm: '1.875rem' } }}
                >
                  5. Subscription plans, billing, and payments (Stripe)
                </Typography>
              </SectionHeader>

              <ContentBox>
                <Box
                  component='ol'
                  sx={{ mb: 0, pl: 3.5, display: 'grid', gap: 1.25 }}
                >
                  <li>
                    <Typography sx={{ fontWeight: 'bold', mb: 1.1 }}>
                      Subscriptions
                    </Typography>
                    <Box
                      component='ol'
                      sx={{ mb: 1.8, pl: 3.8, display: 'grid', gap: 1.1 }}
                    >
                      <li>
                        <Typography
                          variant='body2'
                          sx={{ color: 'text.secondary', lineHeight: 1.7 }}
                        >
                          We offer Subscriptions on a{' '}
                          <strong>daily, monthly, and annual</strong> basis.
                          Plan features, pricing, and inclusions are described
                          on the Website at the time you subscribe.
                        </Typography>
                      </li>
                      <li>
                        <Typography
                          variant='body2'
                          sx={{ color: 'text.secondary', lineHeight: 1.7 }}
                        >
                          Access to paid features and/or paid Content is
                          provided for the Subscription term while your
                          Subscription remains active and in good standing.
                        </Typography>
                      </li>
                    </Box>
                  </li>

                  <li>
                    <Typography sx={{ fontWeight: 'bold', mb: 1.1 }}>
                      Payments and payment processing
                    </Typography>
                    <Box
                      component='ol'
                      sx={{ mb: 1.8, pl: 3.8, display: 'grid', gap: 1.1 }}
                    >
                      <li>
                        <Typography
                          variant='body2'
                          sx={{ color: 'text.secondary', lineHeight: 1.7 }}
                        >
                          Payments are processed through <strong>Stripe</strong>{' '}
                          using an embedded Stripe Elements flow.
                        </Typography>
                      </li>
                      <li>
                        <Typography
                          variant='body2'
                          sx={{ color: 'text.secondary', lineHeight: 1.7 }}
                        >
                          You authorize Stripe to charge your selected payment
                          method for the applicable fees and any applicable
                          charges associated with your Subscription.
                        </Typography>
                      </li>
                      <li>
                        <Typography
                          variant='body2'
                          sx={{ color: 'text.secondary', lineHeight: 1.7 }}
                        >
                          We do not store full payment card details on our
                          systems. Stripe’s processing is governed by Stripe’s
                          terms and policies.
                        </Typography>
                      </li>
                    </Box>
                  </li>

                  <li>
                    <Typography sx={{ fontWeight: 'bold', mb: 1.1 }}>
                      Renewals, cancellations, and changes
                    </Typography>
                    <Box
                      component='ol'
                      sx={{ mb: 1.8, pl: 3.8, display: 'grid', gap: 1.1 }}
                    >
                      <li>
                        <Typography
                          variant='body2'
                          sx={{ color: 'text.secondary', lineHeight: 1.7 }}
                        >
                          Unless stated otherwise at checkout, Subscriptions may
                          renew automatically at the end of the applicable term.
                          You can cancel renewals through your account settings
                          (if enabled) or by contacting us at{' '}
                          <Box
                            component='span'
                            onClick={() =>
                              (window.location.href =
                                'mailto:contact@gcctaxlaws.com')
                            }
                            sx={highlightedLinkStyle}
                          >
                            contact@gcctaxlaws.com
                          </Box>.
                        </Typography>
                      </li>
                      <li>
                        <Typography
                          variant='body2'
                          sx={{ color: 'text.secondary', lineHeight: 1.7 }}
                        >
                          If you cancel, your access typically continues until
                          the end of the current paid term, unless otherwise
                          stated at checkout or required by law.
                        </Typography>
                      </li>
                      <li>
                        <Typography
                          variant='body2'
                          sx={{ color: 'text.secondary', lineHeight: 1.7 }}
                        >
                          We may change prices, plan structures, or features
                          from time to time. Any changes will apply from the
                          next renewal or as otherwise communicated on the
                          Website.
                        </Typography>
                      </li>
                    </Box>
                  </li>

                  <li>
                    <Typography sx={{ fontWeight: 'bold', mb: 1.1 }}>
                      Refunds and chargebacks
                    </Typography>
                    <Box
                      component='ol'
                      sx={{ mb: 1.8, pl: 3.8, display: 'grid', gap: 1.1 }}
                    >
                      <li>
                        <Typography
                          variant='body2'
                          sx={{ color: 'text.secondary', lineHeight: 1.7 }}
                        >
                          Refund eligibility (if any) will be described on the
                          Website and/or at checkout. Unless required by
                          applicable law,{' '}
                          <strong>fees are non-refundable</strong>{' '}
                          once the Subscription term begins, including for
                          partial periods, unused access, or downgrades.
                        </Typography>
                      </li>
                      <li>
                        <Typography
                          variant='body2'
                          sx={{ color: 'text.secondary', lineHeight: 1.7 }}
                        >
                          If you initiate a chargeback or payment dispute, we
                          may suspend access while the dispute is pending and
                          may request additional information to resolve it.
                        </Typography>
                      </li>
                    </Box>
                  </li>

                  <li>
                    <Typography sx={{ fontWeight: 'bold', mb: 1.1 }}>
                      Taxes
                    </Typography>
                    <Typography
                      variant='body2'
                      sx={{ color: 'text.secondary', lineHeight: 1.7, mb: 0.4 }}
                    >
                      VAT is currently not applicable to Claritax Technology
                      Solutions LLC FZ. If tax treatment changes, we may charge
                      applicable taxes as required and collect required billing
                      information to support compliance.
                    </Typography>
                  </li>
                </Box>
              </ContentBox>
            </SectionContainer>

            <SectionContainer id='permitted-use'>
              <SectionHeader>
                <IconContainer>
                  <CheckCircle
                    sx={{ fontSize: { xs: 20, sm: 24 }, color: '#ffda1b' }}
                  />
                </IconContainer>
                <Typography
                  variant='h2'
                  sx={{ fontSize: { xs: '1.5rem', sm: '1.875rem' } }}
                >
                  6. Permitted use and licence
                </Typography>
              </SectionHeader>

              <ContentBox>
                <Box
                  component='ol'
                  sx={{ mb: 0, pl: 3, display: 'grid', gap: 1.25 }}
                >
                  <li>
                    <Typography
                      variant='body2'
                      sx={{ color: 'text.secondary', lineHeight: 1.7 }}
                    >
                      Subject to your compliance with these Terms, we grant you
                      a limited, non-exclusive, non-transferable, revocable
                      licence to access and use the Services and Content for
                      your{' '}
                      <strong>
                        internal informational and research purposes
                      </strong>
                      .
                    </Typography>
                  </li>
                  <li>
                    <Typography
                      variant='body2'
                      sx={{ color: 'text.secondary', lineHeight: 1.7, mb: 1 }}
                    >
                      You must not:
                    </Typography>
                    <Box
                      component='ul'
                      sx={{
                        pl: 3,
                        mb: 1,
                        listStyleType: 'lower-roman',
                        display: 'grid',
                        gap: 1,
                      }}
                    >
                      <li>
                        <Typography
                          variant='body2'
                          sx={{ color: 'text.secondary', lineHeight: 1.7 }}
                        >
                          copy, reproduce, redistribute, republish, transmit,
                          sell, licence, or commercially exploit the Content (or
                          any substantial part of it) except where expressly
                          permitted in writing by us,
                        </Typography>
                      </li>
                      <li>
                        <Typography
                          variant='body2'
                          sx={{ color: 'text.secondary', lineHeight: 1.7 }}
                        >
                          remove or obscure proprietary notices,
                        </Typography>
                      </li>
                      <li>
                        <Typography
                          variant='body2'
                          sx={{ color: 'text.secondary', lineHeight: 1.7 }}
                        >
                          share accounts, credentials, or paid access with
                          unauthorized users,
                        </Typography>
                      </li>
                      <li>
                        <Typography
                          variant='body2'
                          sx={{ color: 'text.secondary', lineHeight: 1.7 }}
                        >
                          use the Content to create a competing product or
                          service, or to build a database or repository that
                          replicates or substitutes the Services.
                        </Typography>
                      </li>
                    </Box>
                  </li>
                </Box>
                <Typography
                  variant='body2'
                  sx={{ color: 'text.secondary', lineHeight: 1.7, mt: 1 }}
                >
                  If you require enterprise, firm-wide, or multi-user access,
                  you must subscribe to an appropriate plan (where offered) or
                  obtain written permission.
                </Typography>
              </ContentBox>
            </SectionContainer>

            <SectionContainer id='prohibited-activities'>
              <SectionHeader>
                <IconContainer>
                  <Block
                    sx={{ fontSize: { xs: 20, sm: 24 }, color: '#ffda1b' }}
                  />
                </IconContainer>
                <Typography
                  variant='h2'
                  sx={{ fontSize: { xs: '1.5rem', sm: '1.875rem' } }}
                >
                  7. Prohibited activities and acceptable use
                </Typography>
              </SectionHeader>

              <ContentBox>
                <Typography
                  variant='body2'
                  sx={{ color: 'text.secondary', lineHeight: 1.7, mb: 1.25 }}
                >
                  You agree not to, and not to attempt to:
                </Typography>
                <Box
                  component='ol'
                  sx={{ mb: 0, pl: 3, display: 'grid', gap: 1.25 }}
                >
                  <li>
                    <Typography
                      variant='body2'
                      sx={{ color: 'text.secondary', lineHeight: 1.7 }}
                    >
                      disrupt, disable, overburden, or impair the Services or
                      interfere with others’ use,
                    </Typography>
                  </li>
                  <li>
                    <Typography
                      variant='body2'
                      sx={{ color: 'text.secondary', lineHeight: 1.7 }}
                    >
                      access the Services using automated means (including bots,
                      scrapers, spiders, crawlers) except where explicitly
                      permitted in writing,
                    </Typography>
                  </li>
                  <li>
                    <Typography
                      variant='body2'
                      sx={{ color: 'text.secondary', lineHeight: 1.7 }}
                    >
                      engage in hacking, probing, penetration attempts, data
                      mining, reverse engineering, or attempts to bypass access
                      controls,
                    </Typography>
                  </li>
                  <li>
                    <Typography
                      variant='body2'
                      sx={{ color: 'text.secondary', lineHeight: 1.7 }}
                    >
                      reproduce, clone, mirror, or create derivative works that
                      replicate the structure, database, or functionality of the
                      Website,
                    </Typography>
                  </li>
                  <li>
                    <Typography
                      variant='body2'
                      sx={{ color: 'text.secondary', lineHeight: 1.7 }}
                    >
                      upload or transmit malware or harmful code,
                    </Typography>
                  </li>
                  <li>
                    <Typography
                      variant='body2'
                      sx={{ color: 'text.secondary', lineHeight: 1.7 }}
                    >
                      use the Services in violation of applicable laws,
                      regulations, or third-party rights (including intellectual
                      property and privacy rights).
                    </Typography>
                  </li>
                </Box>
                <Typography
                  variant='body2'
                  sx={{ color: 'text.secondary', lineHeight: 1.7, mt: 1.25 }}
                >
                  We may suspend or terminate access and pursue legal remedies
                  for prohibited conduct.
                </Typography>
              </ContentBox>
            </SectionContainer>

            <SectionContainer id='intellectual-property'>
              <SectionHeader>
                <IconContainer>
                  <Security
                    sx={{ fontSize: { xs: 20, sm: 24 }, color: '#ffda1b' }}
                  />
                </IconContainer>
                <Typography
                  variant='h2'
                  sx={{ fontSize: { xs: '1.5rem', sm: '1.875rem' } }}
                >
                  8. Intellectual property
                </Typography>
              </SectionHeader>

              <ContentBox>
                <Box
                  component='ol'
                  sx={{ mb: 0, pl: 3, display: 'grid', gap: 1.25 }}
                >
                  <li>
                    <Typography
                      variant='body2'
                      sx={{ color: 'text.secondary', lineHeight: 1.7 }}
                    >
                      The Services and Content are protected by intellectual
                      property laws. Except as expressly stated in these Terms,
                      no rights are granted to you.
                    </Typography>
                  </li>
                  <li>
                    <Typography
                      variant='body2'
                      sx={{ color: 'text.secondary', lineHeight: 1.7 }}
                    >
                      Government-origin materials may be subject to public
                      sector terms or public domain status depending on the
                      source. Regardless, our{' '}
                      <strong>
                        selection, compilation, organisation, database
                        structure, presentation, and any proprietary materials
                      </strong>{' '}
                      remain protected.
                    </Typography>
                  </li>
                  <li>
                    <Typography
                      variant='body2'
                      sx={{ color: 'text.secondary', lineHeight: 1.7 }}
                    >
                      “
                      <Box
                        component='span'
                        onClick={() =>
                          window.open('https://gcctaxlaws.com', '_blank')
                        }
                        sx={highlightedLinkStyle}
                      >
                        GCCTaxLaws.com
                      </Box>
                      ”, “Claritax”, and related names, logos, and identifiers
                      are our trademarks or trade dress (or those of licensors)
                      and may not be used without permission.
                    </Typography>
                  </li>
                </Box>
              </ContentBox>
            </SectionContainer>

            <SectionContainer id='user-content'>
              <SectionHeader>
                <IconContainer>
                  <Message
                    sx={{ fontSize: { xs: 20, sm: 24 }, color: '#ffda1b' }}
                  />
                </IconContainer>
                <Typography
                  variant='h2'
                  sx={{ fontSize: { xs: '1.5rem', sm: '1.875rem' } }}
                >
                  9. User-generated content and feedback
                </Typography>
              </SectionHeader>

              <ContentBox>
                <Typography sx={{ fontWeight: 'bold', mb: 1.25 }}>
                  User-generated content (if enabled)
                </Typography>
                <Typography
                  variant='body2'
                  sx={{ color: 'text.secondary', lineHeight: 1.7, mb: 1.1 }}
                >
                  If the Website allows you to submit content (for example,
                  comments, feedback, or posts):
                </Typography>
                <Box
                  component='ol'
                  sx={{ mb: 2, pl: 3, display: 'grid', gap: 1.1 }}
                >
                  <li>
                    <Typography
                      variant='body2'
                      sx={{ color: 'text.secondary', lineHeight: 1.7 }}
                    >
                      you represent that you have the rights to submit it and
                      that it does not violate laws or third-party rights,
                    </Typography>
                  </li>
                  <li>
                    <Typography
                      variant='body2'
                      sx={{ color: 'text.secondary', lineHeight: 1.7 }}
                    >
                      you grant us a worldwide, non-exclusive, royalty-free,
                      perpetual, irrevocable licence to use, host, store,
                      reproduce, modify, publish, and display that content in
                      connection with operating and improving the Services,
                    </Typography>
                  </li>
                  <li>
                    <Typography
                      variant='body2'
                      sx={{ color: 'text.secondary', lineHeight: 1.7 }}
                    >
                      we may remove content at our discretion.
                    </Typography>
                  </li>
                </Box>
                <Typography sx={{ fontWeight: 'bold', mb: 1.25 }}>
                  Feedback and suggestions
                </Typography>
                <Typography
                  variant='body2'
                  sx={{ color: 'text.secondary', lineHeight: 1.7 }}
                >
                  Any feedback, ideas, or suggestions you provide are
                  non-confidential and may be used by us without restriction or
                  obligation to compensate you.
                </Typography>
              </ContentBox>
            </SectionContainer>

            <SectionContainer id='third-party'>
              <SectionHeader>
                <IconContainer>
                  <Launch
                    sx={{ fontSize: { xs: 20, sm: 24 }, color: '#ffda1b' }}
                  />
                </IconContainer>
                <Typography
                  variant='h2'
                  sx={{ fontSize: { xs: '1.5rem', sm: '1.875rem' } }}
                >
                  10. Third-party links, authentication, and services
                </Typography>
              </SectionHeader>

              <ContentBox>
                <Box
                  component='ol'
                  sx={{ mb: 0, pl: 3, display: 'grid', gap: 1.25 }}
                >
                  <li>
                    <Typography
                      variant='body2'
                      sx={{ color: 'text.secondary', lineHeight: 1.7 }}
                    >
                      The Services may link to third-party websites or include
                      Third-Party Services (for example, Google and LinkedIn for
                      authentication, Stripe for payments).
                    </Typography>
                  </li>
                  <li>
                    <Typography
                      variant='body2'
                      sx={{ color: 'text.secondary', lineHeight: 1.7 }}
                    >
                      We do not control and are not responsible for third-party
                      content, terms, or privacy practices. Your interactions
                      with Third-Party Services are governed by their own terms
                      and policies.
                    </Typography>
                  </li>
                </Box>
              </ContentBox>
            </SectionContainer>

            <SectionContainer id='advertising'>
              <SectionHeader>
                <IconContainer>
                  <Campaign
                    sx={{ fontSize: { xs: 20, sm: 24 }, color: '#ffda1b' }}
                  />
                </IconContainer>
                <Typography
                  variant='h2'
                  sx={{ fontSize: { xs: '1.5rem', sm: '1.875rem' } }}
                >
                  11. Advertising and promotions
                </Typography>
              </SectionHeader>

              <ContentBox>
                <Typography
                  variant='body2'
                  sx={{ color: 'text.secondary', lineHeight: 1.7 }}
                >
                  The Website may include advertisements or promotions for
                  third-party products or services. We do not endorse and are
                  not responsible for third-party offerings. Your dealings with
                  third parties are solely between you and the third party.
                </Typography>
              </ContentBox>
            </SectionContainer>

            <SectionContainer id='disclaimer'>
              <SectionHeader>
                <IconContainer>
                  <Warning
                    sx={{ fontSize: { xs: 20, sm: 24 }, color: '#ffda1b' }}
                  />
                </IconContainer>
                <Typography
                  variant='h2'
                  sx={{ fontSize: { xs: '1.5rem', sm: '1.875rem' } }}
                >
                  12. Disclaimer of warranties
                </Typography>
              </SectionHeader>

              <ContentBox>
                <Box
                  component='ol'
                  sx={{ mb: 0, pl: 3, display: 'grid', gap: 1.25 }}
                >
                  <li>
                    <Typography
                      variant='body2'
                      sx={{ color: 'text.secondary', lineHeight: 1.7 }}
                    >
                      The Services and Content are provided on an{' '}
                      <strong>“as is”</strong>, <strong>“as available”</strong>,
                      and
                      <strong> “best effort”</strong> basis.
                    </Typography>
                  </li>
                  <li>
                    <Typography
                      variant='body2'
                      sx={{ color: 'text.secondary', lineHeight: 1.7 }}
                    >
                      To the fullest extent permitted by law, we disclaim all
                      warranties, whether express, implied, or statutory,
                      including warranties of accuracy, completeness,
                      non-infringement, merchantability, fitness for a
                      particular purpose, and availability.
                    </Typography>
                  </li>
                  <li>
                    <Typography
                      variant='body2'
                      sx={{ color: 'text.secondary', lineHeight: 1.7 }}
                    >
                      Legal and regulatory changes may occur frequently. We do
                      not guarantee that Content is complete, accurate, current,
                      or error-free, or that updates will be reflected
                      immediately (or at all).
                    </Typography>
                  </li>
                </Box>
              </ContentBox>
            </SectionContainer>

            <SectionContainer id='limitation-liability'>
              <SectionHeader>
                <IconContainer>
                  <Shield
                    sx={{ fontSize: { xs: 20, sm: 24 }, color: '#ffda1b' }}
                  />
                </IconContainer>
                <Typography
                  variant='h2'
                  sx={{ fontSize: { xs: '1.5rem', sm: '1.875rem' } }}
                >
                  13. Limitation of liability
                </Typography>
              </SectionHeader>

              <ContentBox>
                <Box
                  component='ol'
                  sx={{ mb: 0, pl: 3, display: 'grid', gap: 1.25 }}
                >
                  <li>
                    <Typography
                      variant='body2'
                      sx={{ color: 'text.secondary', lineHeight: 1.7 }}
                    >
                      To the fullest extent permitted by law,{' '}
                      <strong>
                        <Box
                          component='span'
                          onClick={() =>
                            window.open('https://gcctaxlaws.com', '_blank')
                          }
                          sx={highlightedLinkStyle}
                        >
                          GCCTaxLaws.com
                        </Box>
                        , Claritax Technology Solutions LLC FZ, and our owners,
                        group entities, affiliates, managers, officers,
                        directors, employees, agents, and service providers
                      </strong>{' '}
                      will not be liable for any indirect, incidental,
                      consequential, special, punitive, or exemplary damages
                      arising out of or related to your use of (or inability to
                      use) the Services or Content, even if advised of the
                      possibility of such damages.
                    </Typography>
                  </li>
                  <li>
                    <Typography
                      variant='body2'
                      sx={{ color: 'text.secondary', lineHeight: 1.7 }}
                    >
                      To the fullest extent permitted by law, our aggregate
                      liability for any claim arising out of or related to the
                      Services will not exceed the total fees paid by you to us
                      for the applicable Subscription during the
                      <strong> 12 months</strong> preceding the event giving
                      rise to the claim.
                    </Typography>
                  </li>
                  <li>
                    <Typography
                      variant='body2'
                      sx={{ color: 'text.secondary', lineHeight: 1.7 }}
                    >
                      You acknowledge that you use the Services and Content at
                      your own risk, and that you remain responsible for
                      verifying accuracy and applicability to your facts and
                      jurisdiction.
                    </Typography>
                  </li>
                </Box>
              </ContentBox>
            </SectionContainer>

            <SectionContainer id='indemnification'>
              <SectionHeader>
                <IconContainer>
                  <VerifiedUser
                    sx={{ fontSize: { xs: 20, sm: 24 }, color: '#ffda1b' }}
                  />
                </IconContainer>
                <Typography
                  variant='h2'
                  sx={{ fontSize: { xs: '1.5rem', sm: '1.875rem' } }}
                >
                  14. Indemnification
                </Typography>
              </SectionHeader>

              <ContentBox>
                <Typography
                  variant='body2'
                  sx={{ color: 'text.secondary', lineHeight: 1.7, mb: 1.25 }}
                >
                  You agree to indemnify, defend, and hold harmless
                  GCCTaxLaws.com, Claritax Technology Solutions LLC FZ, and our
                  owners, group entities, affiliates, managers, officers,
                  directors, employees, and agents from and against any claims,
                  liabilities, damages, losses, and expenses (including
                  reasonable legal fees) arising out of or related to:
                </Typography>
                <Box
                  component='ul'
                  sx={{ mb: 0, pl: 3, display: 'grid', gap: 1.1 }}
                >
                  <li>
                    <Typography
                      variant='body2'
                      sx={{ color: 'text.secondary', lineHeight: 1.7 }}
                    >
                      your use of the Services,
                    </Typography>
                  </li>
                  <li>
                    <Typography
                      variant='body2'
                      sx={{ color: 'text.secondary', lineHeight: 1.7 }}
                    >
                      your violation of these Terms,
                    </Typography>
                  </li>
                  <li>
                    <Typography
                      variant='body2'
                      sx={{ color: 'text.secondary', lineHeight: 1.7 }}
                    >
                      your violation of applicable laws or third-party rights,
                    </Typography>
                  </li>
                  <li>
                    <Typography
                      variant='body2'
                      sx={{ color: 'text.secondary', lineHeight: 1.7 }}
                    >
                      your submitted content (if any).
                    </Typography>
                  </li>
                </Box>
              </ContentBox>
            </SectionContainer>

            <SectionContainer id='suspension'>
              <SectionHeader>
                <IconContainer>
                  <PowerOff
                    sx={{ fontSize: { xs: 20, sm: 24 }, color: '#ffda1b' }}
                  />
                </IconContainer>
                <Typography
                  variant='h2'
                  sx={{ fontSize: { xs: '1.5rem', sm: '1.875rem' } }}
                >
                  15. Suspension and termination
                </Typography>
              </SectionHeader>

              <ContentBox>
                <Box
                  component='ol'
                  sx={{ mb: 0, pl: 3, display: 'grid', gap: 1.25 }}
                >
                  <li>
                    <Typography
                      variant='body2'
                      sx={{ color: 'text.secondary', lineHeight: 1.7 }}
                    >
                      We may suspend or terminate your access (including your
                      account and Subscription access) at our sole discretion if
                      we believe you have violated these Terms, engaged in
                      misuse, or created risk or potential legal exposure.
                    </Typography>
                  </li>
                  <li>
                    <Typography
                      variant='body2'
                      sx={{ color: 'text.secondary', lineHeight: 1.7 }}
                    >
                      Termination may be without notice where appropriate (for
                      example, security or abuse cases).
                    </Typography>
                  </li>
                  <li>
                    <Typography
                      variant='body2'
                      sx={{ color: 'text.secondary', lineHeight: 1.7 }}
                    >
                      Upon termination, your right to access the Services ends
                      immediately. Provisions intended to survive (including
                      intellectual property, disclaimers, limitation of
                      liability, indemnity, governing law, and dispute
                      provisions) will survive.
                    </Typography>
                  </li>
                </Box>
              </ContentBox>
            </SectionContainer>

            <SectionContainer id='changes-services'>
              <SectionHeader>
                <IconContainer>
                  <Settings
                    sx={{ fontSize: { xs: 20, sm: 24 }, color: '#ffda1b' }}
                  />
                </IconContainer>
                <Typography
                  variant='h2'
                  sx={{ fontSize: { xs: '1.5rem', sm: '1.875rem' } }}
                >
                  16. Changes to the Services and Content
                </Typography>
              </SectionHeader>

              <ContentBox>
                <Typography
                  variant='body2'
                  sx={{ color: 'text.secondary', lineHeight: 1.7 }}
                >
                  We may modify, update, discontinue, remove, or change any part
                  of the Services or Content at any time, including adding or
                  removing features, changing access rules, or adjusting content
                  coverage, without liability.
                </Typography>
              </ContentBox>
            </SectionContainer>

            <SectionContainer id='beta-services'>
              <SectionHeader>
                <IconContainer>
                  <FlashOn
                    sx={{ fontSize: { xs: 20, sm: 24 }, color: '#ffda1b' }}
                  />
                </IconContainer>
                <Typography
                  variant='h2'
                  sx={{ fontSize: { xs: '1.5rem', sm: '1.875rem' } }}
                >
                  17. Beta services
                </Typography>
              </SectionHeader>

              <ContentBox>
                <Typography
                  variant='body2'
                  sx={{ color: 'text.secondary', lineHeight: 1.7 }}
                >
                  From time to time, we may offer beta features. Beta features
                  are provided “as is” and may be changed or discontinued at any
                  time without notice.
                </Typography>
              </ContentBox>
            </SectionContainer>

            <SectionContainer id='privacy-policy'>
              <SectionHeader>
                <IconContainer>
                  <Visibility
                    sx={{ fontSize: { xs: 20, sm: 24 }, color: '#ffda1b' }}
                  />
                </IconContainer>
                <Typography
                  variant='h2'
                  sx={{ fontSize: { xs: '1.5rem', sm: '1.875rem' } }}
                >
                  18. Privacy policy and cookie policy
                </Typography>
              </SectionHeader>

              <ContentBox>
                <Typography
                  variant='body2'
                  sx={{ color: 'text.secondary', lineHeight: 1.7 }}
                >
                  Your use of the Services is also subject to our{' '}
                  <strong>Privacy Policy</strong> and{' '}
                  <strong>Cookie Policy</strong>, each published separately on
                  the Website.
                </Typography>
              </ContentBox>
            </SectionContainer>

            <SectionContainer id='notices'>
              <SectionHeader>
                <IconContainer>
                  <Notifications
                    sx={{ fontSize: { xs: 20, sm: 24 }, color: '#ffda1b' }}
                  />
                </IconContainer>
                <Typography
                  variant='h2'
                  sx={{ fontSize: { xs: '1.5rem', sm: '1.875rem' } }}
                >
                  19. Notices and communications
                </Typography>
              </SectionHeader>

              <ContentBox>
                <Box
                  component='ol'
                  sx={{ mb: 0, pl: 3, display: 'grid', gap: 1.25 }}
                >
                  <li>
                    <Typography
                      variant='body2'
                      sx={{ color: 'text.secondary', lineHeight: 1.7 }}
                    >
                      You consent to receive communications electronically (for
                      example, by email or by notices posted on the Website).
                    </Typography>
                  </li>
                  <li>
                    <Typography
                      variant='body2'
                      sx={{ color: 'text.secondary', lineHeight: 1.7 }}
                    >
                      We may send notices to the email address associated with
                      your account, and such notices will be deemed received
                      when sent.
                    </Typography>
                  </li>
                  <li>
                    <Typography
                      variant='body2'
                      sx={{ color: 'text.secondary', lineHeight: 1.7 }}
                    >
                      Legal notices to us should be sent to{' '}
                      <Box
                        component='span'
                        onClick={() =>
                          (window.location.href =
                            'mailto:contact@gcctaxlaws.com')
                        }
                        sx={highlightedLinkStyle}
                      >
                        contact@gcctaxlaws.com
                      </Box>.
                    </Typography>
                  </li>
                </Box>
              </ContentBox>
            </SectionContainer>

            <SectionContainer id='governing-law'>
              <SectionHeader>
                <IconContainer>
                  <Gavel
                    sx={{ fontSize: { xs: 20, sm: 24 }, color: '#ffda1b' }}
                  />
                </IconContainer>
                <Typography
                  variant='h2'
                  sx={{ fontSize: { xs: '1.5rem', sm: '1.875rem' } }}
                >
                  20. Governing law and jurisdiction
                </Typography>
              </SectionHeader>

              <ContentBox>
                <Box
                  component='ol'
                  sx={{ mb: 0, pl: 3, display: 'grid', gap: 1.25 }}
                >
                  <li>
                    <Typography
                      variant='body2'
                      sx={{ color: 'text.secondary', lineHeight: 1.7 }}
                    >
                      These Terms are governed by and construed in accordance
                      with the laws of the <strong>United Arab Emirates</strong>
                      .
                    </Typography>
                  </li>
                  <li>
                    <Typography
                      variant='body2'
                      sx={{ color: 'text.secondary', lineHeight: 1.7 }}
                    >
                      Any dispute arising out of or related to these Terms or
                      your use of the Services will be subject to the
                      <strong>
                        {' '}
                        exclusive jurisdiction of the courts of Dubai, United
                        Arab Emirates
                      </strong>
                      .
                    </Typography>
                  </li>
                </Box>
              </ContentBox>
            </SectionContainer>

            <SectionContainer id='severability'>
              <SectionHeader>
                <IconContainer>
                  <Assignment
                    sx={{ fontSize: { xs: 20, sm: 24 }, color: '#ffda1b' }}
                  />
                </IconContainer>
                <Typography
                  variant='h2'
                  sx={{ fontSize: { xs: '1.5rem', sm: '1.875rem' } }}
                >
                  21. Severability
                </Typography>
              </SectionHeader>

              <ContentBox>
                <Typography
                  variant='body2'
                  sx={{ color: 'text.secondary', lineHeight: 1.7 }}
                >
                  If any provision of these Terms is held to be invalid or
                  unenforceable, the remaining provisions will remain in full
                  force and effect.
                </Typography>
              </ContentBox>
            </SectionContainer>

            <SectionContainer id='entire-agreement'>
              <SectionHeader>
                <IconContainer>
                  <Public
                    sx={{ fontSize: { xs: 20, sm: 24 }, color: '#ffda1b' }}
                  />
                </IconContainer>
                <Typography
                  variant='h2'
                  sx={{ fontSize: { xs: '1.5rem', sm: '1.875rem' } }}
                >
                  22. Entire agreement
                </Typography>
              </SectionHeader>

              <ContentBox>
                <Typography
                  variant='body2'
                  sx={{ color: 'text.secondary', lineHeight: 1.7 }}
                >
                  These Terms, together with the Privacy Policy, Cookie Policy,
                  and any other legal notices or policies posted on the Website,
                  constitute the entire agreement between you and us regarding
                  your use of the Services and supersede any prior
                  understandings.
                </Typography>
              </ContentBox>
            </SectionContainer>

            <ContactSection id='contact'>
              <SectionHeader sx={{ mb: { xs: 2, sm: 3 } }}>
                <Box
                  sx={{
                    backgroundColor: themeColors.hover,
                    p: { xs: 1.5, sm: 2 },
                    borderRadius: { xs: 1.5, sm: 2 },
                    mr: { xs: 1.5, sm: 2 },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Email
                    sx={{
                      fontSize: { xs: 20, sm: 24 },
                      color: themeColors.secondary,
                    }}
                  />
                </Box>
                <Typography
                  variant='h2'
                  sx={{
                    fontSize: { xs: '1.5rem', sm: '1.875rem' },
                    color: 'white',
                  }}
                >
                  23. Contact Information
                </Typography>
              </SectionHeader>

              <Typography
                variant='body1'
                sx={{ color: '#e2e8f0', mb: 1, lineHeight: 1.7 }}
              >
                For questions, concerns, or clarifications regarding these
                Terms, contact:
              </Typography>
              <Typography
                variant='body1'
                sx={{ color: 'white', fontWeight: 'bold', lineHeight: 1.7 }}
              >
                Claritax Technology Solutions LLC FZ
              </Typography>
              <Typography
                variant='body1'
                sx={{ color: '#e2e8f0', lineHeight: 1.7 }}
              >
                Meydan Free Zone, Meydan Grandstand, 6th Floor, Al Meydan Rd,
                Nad Al Sheba, Dubai, United Arab Emirates
              </Typography>
              <Typography
                variant='body1'
                sx={{ color: '#e2e8f0', lineHeight: 1.7 }}
              >
                <strong>Email:</strong>{' '}
                <Box
                  component='span'
                  onClick={() =>
                    (window.location.href = 'mailto:contact@gcctaxlaws.com')
                  }
                  sx={linkStyle}
                >
                  contact@gcctaxlaws.com
                </Box>
              </Typography>
            </ContactSection>
          </Container>
        </Box>
      </Box>
    </ThemeProvider>
  )
}

export default TermsAndConditions
